import React, { useState, useEffect } from 'react';

const MazeExplorer = () => {
  const mazeSize = 5;
  const [robotPos, setRobotPos] = useState({ x: 0, y: 0 });
  const [walls, setWalls] = useState([]);
  const [points, setPoints] = useState(0);
  const [penalties, setPenalties] = useState(0);
  const [failCount, setFailCount] = useState(0);
  const [step, setStep] = useState(1);
  const [feedback, setFeedback] = useState('');
  const [explosionCell, setExplosionCell] = useState(null);

  const generateMaze = () => {
    const newWalls = [];
    for (let y = 0; y < mazeSize; y++) {
      for (let x = 0; x < mazeSize; x++) {
        if (
          (x !== 0 || y !== 0) &&
          (x !== mazeSize - 1 || y !== mazeSize - 1) &&
          Math.random() < 0.2
        ) {
          newWalls.push(`${x},${y}`);
        }
      }
    }
    setWalls(newWalls);
    setRobotPos({ x: 0, y: 0 });
    setFeedback('');
    setExplosionCell(null);
    setFailCount(0);
  };

  useEffect(() => {
    generateMaze();
  }, []);

  const updateScore = () => {
    return `Points: ${points} | Penalties: ${penalties}`;
  };

  const move = async (direction) => {
    let { x, y } = robotPos;
    if (direction === 'up' && y > 0) y--;
    if (direction === 'down' && y < mazeSize - 1) y++;
    if (direction === 'left' && x > 0) x--;
    if (direction === 'right' && x < mazeSize - 1) x++;

    const cellKey = `${x},${y}`;

    if (walls.includes(cellKey)) {
      setPenalties((p) => p + 1);
      setFeedback('❌ Penalty: Hit a Mine!');
      setFailCount((f) => f + 1);

      setExplosionCell({ x, y });
      setTimeout(() => setExplosionCell(null), 1000);
      // Robot stays put — do NOT update position
    } else {
      setRobotPos({ x, y });
      setFeedback('');

      if (x === mazeSize - 1 && y === mazeSize - 1) {
        setPoints((p) => p + 1);
        setFeedback('🎉 Reward: Found the Treasure!');
        setTimeout(() => {
          generateMaze();
        }, 2000);
      }
    }
  };

  const cellStyle = {
    width: 60,
    height: 60,
    borderRadius: 8,
    border: '2px solid #FF9800',
    backgroundColor: '#FFF3E0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
  };

  const robotStyle = {
    position: 'absolute',
    width: 60,
    height: 60,
    fontSize: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.3s',
    color: '#4A148C',
    transform: `translate(${robotPos.x * 65}px, ${robotPos.y * 65}px)`,
    zIndex: 2,
  };

  const treasureStyle = {
    ...cellStyle,
    color: '#FFD600',
  };

  return (
    <div style={{ fontFamily: "'Comic Sans MS', cursive", padding: 20 }}>
      {step === 1 ? (
        <div
          style={{
            maxWidth: 900,
            margin: 'auto',
            background: '#fff',
            borderRadius: 16,
            padding: 40,
            boxShadow: '0 0 15px rgba(0,0,0,0.1)',
            animation: 'fadeIn 1s ease',
            color: '#4A148C',
          }}
        >
          <h4 style={{ textAlign: 'center', color: '#F57C00' }}>🤖 Maze Explorer Robot!</h4>
          <img
            src="https://cdn-icons-png.flaticon.com/512/4712/4712054.png"
            alt="Cute Robot"
            style={{ display: 'block', margin: '20px auto', maxWidth: 120 }}
          />
          <div
            style={{
              background: '#FFF3E0',
              padding: 20,
              borderLeft: '8px solid #FF9800',
              borderRadius: 14,
              margin: '20px 0',
              fontSize: '1.1rem',
            }}
          >
            🚀 <strong>What will you do?</strong>
            <br />
            Help our little robot find the treasure hidden in the maze!
            <br />
            <br />
            Click arrows: <strong>⬆️ ⬇️ ⬅️ ➡️</strong> to move the robot.
            <br />
            If the robot hits a mine, it learns and tries again!
            <br />
            If it finds treasure, you earn points!
          </div>
          <div
            style={{
              background: '#FFF3E0',
              padding: 20,
              borderLeft: '8px solid #FF9800',
              borderRadius: 14,
              margin: '20px 0',
              fontSize: '1.1rem',
            }}
          >
            💡 <strong>Why?</strong>
            <br />
            This shows how machines learn through <strong>Trial and Error</strong>.
            <br />
            The robot remembers paths that give rewards (treasure) and forgets paths that don't.
          </div>
          <button
            onClick={() => setStep(2)}
            style={{
              backgroundColor: '#FF9800',
              padding: '12px 30px',
              border: 'none',
              borderRadius: 14,
              fontSize: '1.2rem',
              color: 'white',
              cursor: 'pointer',
              display: 'block',
              margin: '30px auto',
            }}
          >
            Start the Maze Game 🎮
          </button>
        </div>
      ) : (
        <div style={{ maxWidth: 700, margin: 'auto', textAlign: 'center' }}>
          <h4 style={{ color: '#F57C00' }}>🤖 Maze Explorer Robot - Learn by Mistakes!</h4>

          <div style={{ position: 'relative', width: 325, height: 325, margin: '20px auto' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${mazeSize}, 60px)`,
                gridTemplateRows: `repeat(${mazeSize}, 60px)`,
                gap: 5,
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1,
              }}
            >
              {[...Array(mazeSize * mazeSize)].map((_, i) => {
                const x = i % mazeSize;
                const y = Math.floor(i / mazeSize);
                const key = `${x},${y}`;

                if (explosionCell && explosionCell.x === x && explosionCell.y === y) {
                  return (
                    <div
                      key={key}
                      style={{
                        ...cellStyle,
                        backgroundColor: '#FFF3E0',
                        fontSize: '2rem',
                        color: '#D32F2F',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid #F44336',
                        borderRadius: 8,
                      }}
                    >
                      💥
                    </div>
                  );
                }

                if (walls.includes(key)) {
                  return (
                    <div
                      key={key}
                      style={{
                        ...cellStyle,
                        backgroundColor: '#FFF3E0',
                        fontSize: '2rem',
                        color: '#D32F2F',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid #F44336',
                        borderRadius: 8,
                      }}
                    >
                      💥
                    </div>
                  );
                }

                if (x === mazeSize - 1 && y === mazeSize - 1) {
                  return (
                    <div key={key} style={treasureStyle}>
                      💎
                    </div>
                  );
                }
                return <div key={key} style={cellStyle}></div>;
              })}
            </div>
            <div style={robotStyle}>🤖</div>
          </div>

          <div style={{ margin: 20 }}>
            <button style={btnStyle} onClick={() => move('up')}>
              ⬆️ Up
            </button>
            <br />
            <button style={btnStyle} onClick={() => move('left')}>
              ⬅️ Left
            </button>
            <button style={btnStyle} onClick={() => move('down')}>
              ⬇️ Down
            </button>
            <button style={btnStyle} onClick={() => move('right')}>
              ➡️ Right
            </button>
          </div>

          <div style={{ marginTop: 10, fontSize: '1.2rem', color: '#D81B60' }}>{feedback}</div>
          <div style={{ fontSize: '1.2rem', marginTop: 10 }}>{updateScore()}</div>
        </div>
      )}
    </div>
  );
};

const btnStyle = {
  backgroundColor: '#FF9800',
  padding: '10px 20px',
  margin: '10px',
  border: 'none',
  color: 'white',
  borderRadius: 12,
  cursor: 'pointer',
  fontSize: '1rem',
};

export default MazeExplorer;
